// components/salidas/EditarApoyoModal.js
// Corrige un lote ya registrado de "apoyo a misioneros": fecha, monto y a quiénes se les entregó.
// El título (a qué se refiere la salida) no se puede cambiar aquí: para eso hay que desactivar el
// registro y crear uno nuevo desde "+ Nueva salida".
import { useEffect, useMemo, useState } from "react";
import { FaExclamationTriangle, FaPlus, FaTimes } from "react-icons/fa";
import Modal from "../Modal";

const TITULO_OFRENDA_AMOR = "Ofrenda de amor";
const DESTINO_VACIO = { tipo: "", id_misionero: "", nombre: "", monto: "" };

const API = "/api";

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const clasesInput =
  "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none";

const APARTADOS = [
  { clave: "misiones", etiqueta: "Misiones" },
  { clave: "biblia", etiqueta: "Biblia" },
  { clave: "especial", etiqueta: "Especial" },
];

export default function EditarApoyoModal({ open, salida, onClose, onGuardado }) {
  const [cargando, setCargando] = useState(false);
  const [misioneros, setMisioneros] = useState([]);
  const [fecha, setFecha] = useState("");
  const [monto, setMonto] = useState("");
  const [seleccionados, setSeleccionados] = useState(new Set());
  // Solo para "Ofrenda de amor": lista de destinatarios (registrados o no), igual que al registrar.
  const [destinos, setDestinos] = useState([]);
  const [disponible, setDisponible] = useState({ misiones: 0, biblia: 0, especial: 0 });
  const [apartados, setApartados] = useState(new Set());
  const [montosApartado, setMontosApartado] = useState({});
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const esOfrendaAmor = salida?.titulo === TITULO_OFRENDA_AMOR;

  useEffect(() => {
    if (!open || !salida) return;
    setError("");
    setFecha(salida.fecha);
    setMonto(String(salida.monto_total));
    const esOfrenda = salida.titulo === TITULO_OFRENDA_AMOR;
    if (esOfrenda) {
      setDestinos(
        salida.misioneros.map((m) => ({
          tipo: m.id_misionero ? "registrado" : "libre",
          id_misionero: m.id_misionero || "",
          nombre: m.id_misionero ? "" : m.nombre,
          monto: String(m.monto),
        }))
      );
    } else {
      setSeleccionados(new Set(salida.misioneros.map((m) => m.id_misionero)));
    }
    const apartadosActuales = salida.apartados || [];
    setApartados(new Set(apartadosActuales.map((a) => a.apartado)));
    setMontosApartado(Object.fromEntries(apartadosActuales.map((a) => [a.apartado, String(a.monto)])));
    (async () => {
      setCargando(true);
      try {
        const [resMisioneros, resDisponible] = await Promise.all([
          fetch(`${API}/misioneros/activos`, { credentials: "include" }),
          fetch(`${API}/apoyo-misioneros/disponible`, { credentials: "include" }),
        ]);
        if (!resMisioneros.ok) throw new Error("Error obteniendo misioneros");
        const todos = await resMisioneros.json();
        // En Ofrenda de amor el destinatario puede ser cualquier misionero (no solo de un título).
        setMisioneros(esOfrenda ? todos : todos.filter((m) => (m.titulo || "Sin título") === salida.titulo));
        if (resDisponible.ok) {
          const d = await resDisponible.json();
          // Lo que esta salida ya tenía apartado se le devuelve al disponible: se está
          // reemplazando, no sumando encima.
          apartadosActuales.forEach((a) => { d[a.apartado] = Number(d[a.apartado] || 0) + Number(a.monto); });
          setDisponible(d);
        }
      } catch (err) {
        console.error(err);
        setError("No se pudo cargar la lista de misioneros");
      } finally {
        setCargando(false);
      }
    })();
  }, [open, salida]);

  const misionerosOrdenados = useMemo(() => [...misioneros].sort((a, b) => a.nombre.localeCompare(b.nombre)), [misioneros]);

  const agregarDestino = () => setDestinos((d) => [...d, { ...DESTINO_VACIO }]);
  const quitarDestino = (idx) => setDestinos((d) => (d.length === 1 ? [{ ...DESTINO_VACIO }] : d.filter((_, i) => i !== idx)));
  const cambiarMontoDestino = (idx, valor) => setDestinos((d) => d.map((x, i) => (i === idx ? { ...x, monto: valor } : x)));
  const cambiarNombreDestino = (idx, valor) => setDestinos((d) => d.map((x, i) => (i === idx ? { ...x, nombre: valor } : x)));
  const elegirEnDestino = (idx, valor) =>
    setDestinos((d) =>
      d.map((x, i) => {
        if (i !== idx) return x;
        if (valor === "__libre__") return { ...x, tipo: "libre", id_misionero: "" };
        return { ...x, tipo: "registrado", id_misionero: valor, nombre: "" };
      })
    );

  const destinosUtiles = destinos.filter((d) => ((d.tipo === "registrado" && d.id_misionero) || (d.tipo === "libre" && d.nombre.trim())) && d.monto);
  const sumaDestinos = destinosUtiles.reduce((s, d) => s + (Number(d.monto) || 0), 0);

  const alternarApartado = (clave) =>
    setApartados((s) => {
      const n = new Set(s);
      n.has(clave) ? n.delete(clave) : n.add(clave);
      return n;
    });

  const cambiarMontoApartado = (clave, valor) => setMontosApartado((s) => ({ ...s, [clave]: valor }));

  // Igual que al registrar: con un solo apartado marcado, sale completo de ahí.
  const apartadosEfectivos = () => {
    const lista = [...apartados];
    if (lista.length === 0) return [];
    if (lista.length === 1) return [{ apartado: lista[0], monto: Number(monto) || 0 }];
    return lista.map((clave) => ({ apartado: clave, monto: Number(montosApartado[clave]) || 0 }));
  };

  // Quien ya no esté activo no se puede volver a seleccionar: se quita del lote al guardar.
  const yaNoActivos = useMemo(
    () => (salida?.misioneros || []).filter((m) => !misioneros.some((a) => a.id_misionero === m.id_misionero)),
    [salida, misioneros]
  );

  const suma = misioneros.filter((m) => seleccionados.has(m.id_misionero)).reduce((s, m) => s + Number(m.cantidad_apoyo_mensual || 0), 0);
  const montoNum = Number(monto);
  const coincide = monto !== "" && Math.abs(suma - montoNum) <= 0.009;
  const sumaApartados = apartadosEfectivos().reduce((s, a) => s + a.monto, 0);
  const apartadosCoinciden = apartados.size > 0 && monto !== "" && Math.abs(sumaApartados - montoNum) <= 0.009;
  const excedeAlgunApartado = apartadosEfectivos().some((a) => a.monto - Number(disponible[a.apartado] || 0) > 0.009);

  const alternar = (id) =>
    setSeleccionados((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const guardar = async () => {
    if (!fecha) return setError("Indica la fecha");
    if (!(montoNum > 0)) return setError("Indica el monto");

    let payloadExtra;
    if (esOfrendaAmor) {
      if (destinosUtiles.length === 0) return setError("Agrega al menos una persona");
      if (Math.abs(sumaDestinos - montoNum) > 0.009) {
        return setError(`El monto (${formatoMoneda(montoNum)}) no coincide con la suma de las personas (${formatoMoneda(sumaDestinos)})`);
      }
      payloadExtra = {
        destinos: destinosUtiles.map((d) =>
          d.tipo === "registrado" ? { id_misionero: Number(d.id_misionero), monto: Number(d.monto) } : { nombre: d.nombre.trim(), monto: Number(d.monto) }
        ),
      };
    } else {
      if (seleccionados.size === 0) return setError("Selecciona al menos un misionero");
      if (!coincide) return setError(`El monto (${formatoMoneda(montoNum)}) no coincide con la suma de los seleccionados (${formatoMoneda(suma)})`);
      payloadExtra = { ids_misioneros: [...seleccionados] };
    }
    if (apartados.size === 0) return setError("Indica de dónde sale el dinero (Misiones, Biblia o Especial)");
    if (!apartadosCoinciden) return setError(`Lo capturado por apartado (${formatoMoneda(sumaApartados)}) no coincide con el monto (${formatoMoneda(montoNum)})`);
    if (excedeAlgunApartado) return setError("Lo solicitado excede el disponible de algún apartado");

    setGuardando(true);
    setError("");
    try {
      const res = await fetch(`${API}/apoyo-misioneros/salida/${salida.id_salida}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fecha, monto_total: montoNum, apartados: apartadosEfectivos(), ...payloadExtra }),
      });
      const cuerpo = await res.json().catch(() => ({}));
      if (!res.ok) return setError(cuerpo.mensaje || "Error guardando los cambios");
      onGuardado?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError("Error inesperado al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Editar: ${salida?.titulo || ""}`} maxWidth="max-w-lg">
      {cargando ? (
        <p className="text-center text-slate-400 py-10">Cargando...</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 justify-end">
            {APARTADOS.map(({ clave, etiqueta }) => (
              <div key={clave} className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-right">
                <p className="text-slate-400">{etiqueta} disponible</p>
                <p className="font-semibold text-slate-700 tabular-nums">{formatoMoneda(disponible[clave])}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Fecha <span className="text-rose-500">*</span></label>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={clasesInput} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Monto total <span className="text-rose-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <input type="number" min="0" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} className={`${clasesInput} pl-6`} />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">¿De dónde sale el dinero?</label>
            <div className="flex flex-wrap gap-x-4 gap-y-2 border border-slate-200 rounded-lg p-2.5">
              {APARTADOS.map(({ clave, etiqueta }) => (
                <label key={clave} className="flex items-center gap-1.5 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={apartados.has(clave)}
                    onChange={() => alternarApartado(clave)}
                    className="w-4 h-4 accent-indigo-600"
                  />
                  {etiqueta}
                  {apartados.size > 1 && apartados.has(clave) && (
                    <span className="relative ml-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={montosApartado[clave] || ""}
                        onChange={(e) => cambiarMontoApartado(clave, e.target.value)}
                        placeholder="0.00"
                        className="w-24 border border-slate-200 rounded-lg pl-5 pr-2 py-1 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                      />
                    </span>
                  )}
                </label>
              ))}
            </div>
            {apartados.size > 0 && monto !== "" && (
              <p className={`text-xs mt-1.5 ${apartadosCoinciden ? "text-emerald-600" : "text-rose-600"}`}>
                Capturado por apartado: {formatoMoneda(sumaApartados)} de {formatoMoneda(montoNum)}
              </p>
            )}
          </div>

          {esOfrendaAmor ? (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Ofrenda de amor</label>
              <div className="space-y-2">
                {destinos.map((d, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <select
                      value={d.tipo === "libre" ? "__libre__" : d.id_misionero}
                      onChange={(e) => elegirEnDestino(idx, e.target.value)}
                      className={`${clasesInput} flex-1 min-w-[160px]`}
                    >
                      <option value="">-- Selecciona un misionero --</option>
                      {misionerosOrdenados.map((m) => (
                        <option key={m.id_misionero} value={m.id_misionero}>{m.nombre}</option>
                      ))}
                      <option value="__libre__">Persona no registrada...</option>
                    </select>
                    {d.tipo === "libre" && (
                      <input
                        type="text"
                        value={d.nombre}
                        onChange={(e) => cambiarNombreDestino(idx, e.target.value)}
                        placeholder="Nombre completo"
                        maxLength={150}
                        className={`${clasesInput} flex-1 min-w-[140px]`}
                      />
                    )}
                    <div className="relative w-28 shrink-0">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={d.monto}
                        onChange={(e) => cambiarMontoDestino(idx, e.target.value)}
                        placeholder="0.00"
                        className={`${clasesInput} pl-6`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => quitarDestino(idx)}
                      title="Quitar"
                      className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                    >
                      <FaTimes className="text-xs" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={agregarDestino} className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1">
                <FaPlus className="text-[10px]" /> Agregar persona
              </button>
              <p className={`text-xs mt-1.5 ${monto !== "" && destinosUtiles.length > 0 && Math.abs(sumaDestinos - montoNum) > 0.009 ? "text-rose-600" : destinosUtiles.length > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                Capturado: {formatoMoneda(sumaDestinos)}{monto !== "" ? ` de ${formatoMoneda(montoNum)}` : ""}
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {salida?.titulo} <span className="text-slate-400 font-normal">({seleccionados.size})</span>
              </label>
              {misioneros.length === 0 ? (
                <p className="text-sm text-slate-400 py-3">No hay misioneros activos con este título.</p>
              ) : (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                  {misioneros.map((m) => (
                    <li key={m.id_misionero} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={seleccionados.has(m.id_misionero)}
                        disabled={m.cantidad_apoyo_mensual === null || m.cantidad_apoyo_mensual === undefined}
                        onChange={() => alternar(m.id_misionero)}
                        aria-label={`Entregar a ${m.nombre}`}
                        className="w-4 h-4 accent-indigo-600 disabled:cursor-not-allowed"
                      />
                      <span className="flex-1 truncate text-slate-700">{m.nombre}</span>
                      <span className="text-slate-500 tabular-nums">
                        {m.cantidad_apoyo_mensual === null || m.cantidad_apoyo_mensual === undefined
                          ? <span className="text-amber-600">sin monto configurado</span>
                          : formatoMoneda(m.cantidad_apoyo_mensual)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className={`text-xs mt-1.5 ${monto !== "" && seleccionados.size > 0 && !coincide ? "text-rose-600" : seleccionados.size > 0 ? "text-emerald-600" : "text-slate-400"}`}>
                Seleccionado: {formatoMoneda(suma)}{monto !== "" ? ` de ${formatoMoneda(montoNum)}` : ""}
              </p>
              {yaNoActivos.length > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {yaNoActivos.map((m) => m.nombre).join(", ")} ya no {yaNoActivos.length === 1 ? "está activo" : "están activos"} y se {yaNoActivos.length === 1 ? "quitará" : "quitarán"} de este registro al guardar.
                </p>
              )}
            </div>
          )}

          {excedeAlgunApartado && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <FaExclamationTriangle className="shrink-0 mt-0.5 text-xs" /> Lo que estás repartiendo excede el disponible de algún apartado.
            </p>
          )}

          {error && (
            <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 flex items-start gap-2">
              <FaExclamationTriangle className="shrink-0 mt-0.5 text-xs" /> {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button onClick={onClose} className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
              Cancelar
            </button>
            <button
              onClick={guardar}
              disabled={guardando || excedeAlgunApartado}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50"
            >
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
